'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  industry: string;
  service: string;
  message: string;
}

const ConsultationModal = ({ isOpen, onClose }: ConsultationModalProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    setTimeout(() => {
      setSubmitSuccess(false);
      onClose();
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        service: '',
        message: ''
      });
    }, 2000);
  };

  if (!isOpen || !isHydrated) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-2xl shadow-elevated max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primary to-secondary text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-headline font-bold text-2xl mb-2">
                Get Your Free IT Assessment
              </h2>
              <p className="text-sm text-white/90 font-body">
                Let&apos;s discuss how we can help your business thrive
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors duration-200"
              aria-label="Close modal"
            >
              <Icon name="XMarkIcon" size={24} variant="outline" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Success Message */}
          {submitSuccess && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center space-x-3">
              <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success" />
              <div>
                <p className="font-cta font-bold text-success">Request Submitted Successfully!</p>
                <p className="text-sm text-success/80 font-body">We&apos;ll contact you within 24 hours.</p>
              </div>
            </div>
          )}

          {/* Name & Email */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-cta font-semibold text-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-body text-foreground"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-cta font-semibold text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-body text-foreground"
                placeholder="john@company.com"
              />
            </div>
          </div>

          {/* Phone & Company */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-cta font-semibold text-foreground mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-body text-foreground"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label htmlFor="company" className="block text-sm font-cta font-semibold text-foreground mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-body text-foreground"
                placeholder="Your Company Inc"
              />
            </div>
          </div>

          {/* Industry & Service */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="industry" className="block text-sm font-cta font-semibold text-foreground mb-2">
                Industry *
              </label>
              <select
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-body text-foreground"
              >
                <option value="">Select Industry</option>
                <option value="healthcare">Healthcare</option>
                <option value="finance">Financial Services</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="retail">Retail</option>
                <option value="education">Education</option>
                <option value="small-business">Small Business</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="service" className="block text-sm font-cta font-semibold text-foreground mb-2">
                Service Interest *
              </label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-body text-foreground"
              >
                <option value="">Select Service</option>
                <option value="infrastructure">IT Infrastructure</option>
                <option value="cybersecurity">Cybersecurity</option>
                <option value="cloud">Cloud Solutions</option>
                <option value="managed">Managed Services</option>
                <option value="consulting">IT Consulting</option>
                <option value="support">Emergency Support</option>
              </select>
            </div>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-cta font-semibold text-foreground mb-2">
              Tell us about your IT needs
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-body text-foreground resize-none"
              placeholder="Describe your current IT challenges or goals..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-accent hover:bg-accent/90 disabled:bg-accent/50 text-white rounded-lg font-cta font-bold text-lg shadow-card transition-all duration-300"
          >
            {isSubmitting ? (
              <>
                <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Icon name="PaperAirplaneIcon" size={20} variant="outline" />
                <span>Request Free Assessment</span>
              </>
            )}
          </button>

          {/* Privacy Note */}
          <p className="text-xs text-muted-foreground font-body text-center">
            By submitting this form, you agree to our privacy policy. We&apos;ll never share your information.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ConsultationModal;